import { Key, KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { OpInput } from 'new-components/DLS/OpInput/OpInput';
import { SelectFilter } from './SelectFilter';
import { OpTableRawColumnType } from '../types';
import { DataFetchSelectFilter } from './DataFetchSelectFilter';

export type OnFilterUpdateType = (
  filterKey: Key,
  filterValue: string[] | null,
) => void;

export interface ColumnHeaderFilterProps
  extends Pick<OpTableRawColumnType, 'filter' | 'defaultFilteredValue'> {
  filterKey: Key;
  testId?: string;
  onFilterUpdate?: OnFilterUpdateType;
  resetRowSelections?: () => void;
  gtm?: string;
  isClientSide?: boolean;
}

// Escape offending characters
const sanitizeFilterValue = (filterValue: string) =>
  filterValue.replace(/[\\()\\[\]]/g, '\\$&');

// Unescape offending characters
const unsanitizeFilterValue = (filterValue: string) =>
  filterValue.replace(/\\(.)/g, '$1');

export const ColumnHeaderFilter = ({
  filterKey,
  onFilterUpdate,
  resetRowSelections = () => {},
  filter,
  testId,
  defaultFilteredValue,
  gtm,
  isClientSide = false,
}: ColumnHeaderFilterProps) => {
  const { t } = useTranslation();

  // Disallows using whitespace-only filters on this input
  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.code === 'Space' && e.currentTarget.value.trim() === '') {
      e.preventDefault();
    }
  };

  const createChangeHandler = (filterValue?: string | string[]) => {
    let finalFilterValue: string[] | null;
    /**
     * Helium uses default match logic which is “if it looks like a number then do a number
     * match, otherwise do a string match". Since we explicitly want a substring match for input filters, we
     * should be sending an explicit substring-match operator ~ to helium so it will always
     * use string matching, not number matching. Since we always want exact match for select filters, we should
     * be using an explicit exact match operator = so that it will always use exact match whether the option value is a number or a string.
     */
    const filterSearchOperator = filter?.type === 'input' ? '~' : '=';
    if (Array.isArray(filterValue)) {
      if (filterValue.length === 0) {
        // If no filters are selected, we shouldn't filter at all
        finalFilterValue = null;
      } else {
        finalFilterValue = filterValue.map((v) =>
          isClientSide ? v : `${filterSearchOperator}${v}`,
        ); // use explicit substring match for inputs in case searching on a string that is only numbers
      }
    } else if (typeof filterValue === 'string' && filterValue !== '') {
      finalFilterValue = isClientSide
        ? [filterValue]
        : [sanitizeFilterValue(`${filterSearchOperator}${filterValue}`)];
    } else {
      finalFilterValue = null;
    }

    if (onFilterUpdate) {
      onFilterUpdate(filterKey, finalFilterValue);
    }

    // Reset row selections on filter
    resetRowSelections();
  };

  // This filter's purpose is to gracefully handle large data sets for filterable options for the table. It allows for searching the options and pulling in more than Heliums limit as needed.
  if (filter?.type === 'dataFetchSelect') {
    const defaultValue =
      defaultFilteredValue === null
        ? undefined
        : defaultFilteredValue?.map((v) =>
            v.startsWith('~') || v.startsWith('=') ? v.slice(1) : v,
          );

    const handleChange = (value: string[]) => {
      createChangeHandler(value ? String(value) : undefined);
    };

    return (
      <DataFetchSelectFilter
        onChange={handleChange}
        key={defaultFilteredValue?.toString()}
        defaultValue={defaultValue?.[0].split(',')}
        queryOptions={filter.queryOptions}
        filterSort={filter.filterSort}
        pathToLabel={filter.pathToLabel}
        pathToTooltip={filter.pathToTooltip}
        pathToValue={filter.pathToValue}
      />
    );
  }

  if (filter?.type === 'select' || filter?.type === 'multiSelect') {
    const defaultValue =
      defaultFilteredValue === null
        ? undefined
        : defaultFilteredValue?.map((v) =>
            v.startsWith('~') || v.startsWith('=') ? v.slice(1) : v,
          );

    return (
      <SelectFilter
        /* This will allow the select to get the proper defaultFilteredValue on initial load - making sure to display the final default value */

        key={defaultFilteredValue?.toString()}
        gtm={gtm}
        testId={testId}
        allowMultiple={filter.type === 'multiSelect'}
        options={filter?.options || []}
        defaultValue={defaultValue}
        onChange={(value) => {
          createChangeHandler(value);
        }}
      />
    );
  }

  if (filter?.type === 'input') {
    /**
     * Antd defaultFilteredValue is an array but we only want to use the first value
     * in the array for inputs
     *
     * For data fetching tables we prefix filters with a `~` to indicate a substring match
     * We passing in defaultFilteredValues we want to make sure to remove the `~` so the
     * user doesn't see it
     */
    const defaultValue = defaultFilteredValue?.[0]
      ? defaultFilteredValue[0].startsWith('~') ||
        defaultFilteredValue[0].startsWith('=')
        ? defaultFilteredValue[0].slice(1)
        : defaultFilteredValue[0]
      : undefined;

    return (
      <OpInput
        /* This will allow input to get the proper defaultFilteredValue on initial load -  making sure to display the final default value */
        key={defaultFilteredValue?.toString()}
        gtm={gtm && `${gtm}-input-filter`}
        size="small"
        placeholder={t('Filter')}
        testId={testId}
        allowClear
        data-lpignore // Prevents autofill
        defaultValue={
          defaultValue ? unsanitizeFilterValue(defaultValue) : undefined
        }
        onChange={({ target: { value } }) => {
          createChangeHandler(value);
        }}
        onKeyDown={handleKeyDown}
      />
    );
  }

  return <OpInput size="small" placeholder={t('Unfilterable')} disabled />;
};
